import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowRight, Receipt, Camera, FileSpreadsheet, PhoneCall, ShieldCheck } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import LaunchOfferBanner from "@/components/LaunchOfferBanner";
import PhoneMockup from "@/components/PhoneMockup";
import usePageMeta from "@/lib/usePageMeta";
import GuaranteeBadge from "@/components/GuaranteeBadge";
import ComparisonTable from "@/components/ComparisonTable";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};
const stagger = { animate: { transition: { staggerChildren: 0.08 } } };

// ---------- Hero (Stripe/Linear-style: clean, focused, one CTA primary) ----------
function Hero() {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 400], [0, 40]);
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-c/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary-c/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-14 sm:pb-20 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <motion.div variants={stagger} initial="initial" animate="animate">
          <motion.span variants={{ initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-c text-[11px] sm:text-xs font-semibold text-primary-c shadow-sm">
            {t("hero.badge")}
          </motion.span>
          <motion.h1
            variants={{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-[38px] leading-[1.02] sm:text-5xl md:text-[64px] mt-4 sm:mt-5 tracking-tight"
          >
            {t("home.hero_line1")} <br className="hidden sm:block" />
            <span className="font-serif-italic text-primary-c">{t("home.hero_accent")}</span>
          </motion.h1>
          <motion.p
            variants={{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-base sm:text-lg text-text-secondary max-w-xl leading-relaxed"
          >
            {t("home.hero_sub")} <strong className="text-[var(--text)]">{t("home.hero_sub_highlight")}</strong>
          </motion.p>
          <motion.div
            variants={{ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-6 sm:mt-7 flex flex-col sm:flex-row gap-3"
          >
            <Link to="/register" data-testid="hero-cta-trial" className="btn-primary text-center inline-flex items-center justify-center gap-2 group">
              {t("home.hero_cta_primary")}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/reserver" data-testid="hero-cta-demo" className="btn-secondary text-center inline-flex items-center justify-center gap-2">
              <PhoneCall className="w-4 h-4" /> {t("home.hero_cta_secondary")}
            </Link>
          </motion.div>
          <motion.div
            variants={{ initial: { opacity: 0 }, animate: { opacity: 1 } }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-text-secondary"
          >
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary-c" /> {t("hero.no_card")}</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-primary-c" /> {t("hero.cancel_anytime")}</span>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative">
          <PhoneMockup alt={t("home.screens_alt")} />
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.6 }} className="absolute bottom-2 left-0 sm:-left-2 z-20 card-c w-48 sm:w-60 !p-3 sm:!p-4">
            <p className="text-[10px] sm:text-xs text-text-secondary font-medium uppercase tracking-wide">{t("hero.card_day")}</p>
            <p className="font-display text-2xl sm:text-3xl mt-1">CHF 2'140<span className="text-secondary-c">.00</span></p>
            <p className="text-[10px] sm:text-[11px] text-text-secondary mt-1">{t("hero.card_vat")} : <strong className="text-primary-c">160.35 CHF</strong></p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.8 }} className="hidden md:block absolute top-2 left-6 z-20 card-c w-40 sm:w-52 !p-3 sm:!p-4">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-secondary-c" />
              <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-text-secondary">{t("hero.card_declaration")}</p>
            </div>
            <p className="font-display text-xl sm:text-2xl mt-1">1'831<span className="text-secondary-c">.85</span></p>
            <p className="text-[10px] text-text-secondary mt-0.5">{t("hero.card_net")}</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ---------- Trust logos strip ----------
function TrustStrip() {
  const { t } = useTranslation();
  return (
    <motion.div {...fadeUp} className="border-y border-c bg-white/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[11px] sm:text-xs uppercase tracking-wider text-text-secondary font-semibold">
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-primary-c" /> {t("trust.afc")}</span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-primary-c" /> {t("trust.tls")}</span>
        <span className="inline-flex items-center gap-1.5">{t("trust.made_swiss")}</span>
      </div>
    </motion.div>
  );
}

// ---------- 3 Big value props (Linear-style clean cards) ----------
function ValueProps() {
  const { t } = useTranslation();
  const props = [
    { icon: Receipt, k: "value_prop_1", link: "/produit" },
    { icon: Camera, k: "value_prop_2", link: "/produit" },
    { icon: FileSpreadsheet, k: "value_prop_3", link: "/produit" },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <motion.div {...fadeUp} className="grid sm:grid-cols-3 gap-4">
        {props.map((p, i) => (
          <motion.div
            key={p.k}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: i * 0.08 }}
            whileHover={{ y: -4 }}
            className="card-c group cursor-pointer"
            data-testid={`home-vp-${i}`}
          >
            <div className="w-11 h-11 rounded-2xl bg-primary-c/10 flex items-center justify-center">
              <p.icon className="w-5 h-5 text-primary-c" strokeWidth={1.7} />
            </div>
            <h3 className="mt-4 font-display text-xl">{t(`home.${p.k}`)}</h3>
            <p className="text-sm text-text-secondary mt-1">{t(`home.${p.k}_desc`)}</p>
            <Link to={p.link} className="inline-flex items-center gap-1 text-xs font-semibold text-primary-c mt-4 group-hover:gap-2 transition-all">
              {t("home.explore_product")} <ArrowRight className="w-3 h-3" />
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

// ---------- Mobile sticky trial button ----------
function StickyTrialCTA() {
  const { t } = useTranslation();
  return (
    <div className="md:hidden fixed inset-x-0 bottom-0 z-40 px-4 pt-2 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)] to-transparent" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
      <Link to="/register" data-testid="sticky-trial-cta" className="btn-primary w-full inline-flex items-center justify-center gap-2 shadow-lg">
        {t("home.sticky_trial")} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

// ---------- Early users invitation ----------
function EarlyUsers() {
  const { t } = useTranslation();
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20" data-testid="early-users-section">
      <motion.div {...fadeUp} className="rounded-3xl bg-surface-muted p-8 sm:p-12 grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl leading-tight">
            {t("early.title")} <span className="font-serif-italic text-primary-c">{t("early.title_accent")}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-text-secondary max-w-xl leading-relaxed">{t("early.sub")}</p>
        </div>
        <div className="flex flex-col gap-3">
          <Link to="/register" data-testid="early-users-cta" className="btn-primary inline-flex items-center justify-center gap-2 group">
            {t("early.cta")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/reserver" data-testid="early-users-call" className="btn-secondary text-center">{t("early.cta_call")}</Link>
        </div>
      </motion.div>
    </section>
  );
}

// ---------- Final CTA ----------
function FinalCta() {
  const { t } = useTranslation();
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <motion.div {...fadeUp} className="rounded-3xl p-8 sm:p-14 text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg, #F5EFDE 0%, #E8F1EA 100%)" }}>
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-primary-c/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-secondary-c/10 rounded-full blur-3xl" />
        <div className="relative">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl leading-tight">
            {t("final_cta.title_1")} <span className="font-serif-italic text-primary-c">{t("final_cta.title_accent")}</span> {t("final_cta.title_2")}
          </h2>
          <p className="mt-4 text-text-secondary max-w-xl mx-auto text-sm sm:text-base">{t("final_cta.sub")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="final-cta" className="btn-primary inline-flex items-center justify-center gap-2 group">
              {t("final_cta.primary")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/reserver" data-testid="final-cta-contact" className="btn-secondary inline-flex items-center justify-center gap-2">
              {t("final_cta.secondary")}
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

// ---------- Sticky mobile CTA ----------
function StickyMobileCta() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const on = () => setVisible(window.scrollY > 600);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  if (!visible) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-c shadow-2xl p-3" data-testid="sticky-mobile-cta">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary-c">{t("sticky.title")}</p>
          <p className="text-xs text-text-secondary">{t("sticky.sub")}</p>
        </div>
        <Link to="/register" data-testid="sticky-cta-btn" className="btn-primary !py-2 !px-4 text-sm shrink-0">
          {t("sticky.cta")}
        </Link>
      </div>
    </div>
  );
}

// ---------- Landing (Home) ----------
export default function Landing() {
  usePageMeta("home");
  return (
    <div className="min-h-screen bg-bg text-[var(--text)] pb-24 md:pb-0">
      <LaunchOfferBanner />
      <PublicHeader />
      <Hero />
      <TrustStrip />
      <ValueProps />
      <ComparisonTable />
      <EarlyUsers />
      <StickyTrialCTA />
      <GuaranteeBadge />
      <FinalCta />
      <StickyMobileCta />
      <PublicFooter />
    </div>
  );
}
